import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileCheck, TrendingUp, Brain, Target, Shield, Zap, ArrowRight, Sparkles, Globe, Send, Languages, Mic, Plane, Wrench, Home, Clock, ClipboardCheck, Users, GraduationCap, Award } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoginModal from '../components/LoginModal';
import SEO from '../components/SEO';
import './HowItWorksPage.css';

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  return (
    <div className="hiw">
      <SEO
        title="Nasıl Çalışır?"
        description="isBulsun.com yapay zeka ile nasıl çalışır? Akıllı eşleştirme, otomatik başvuru, CV optimizasyonu, kariyer gelişimi ve yurtdışı remote iş ilanları."
        path="/nasil-calisir"
      />
      <section className="hiw__hero">
        <h1 className="hiw__title">
          Yapay Zeka <span className="hiw__title-accent">Senin İçin</span> Çalışır
        </h1>
        <p className="hiw__subtitle">
          Geleneksel iş arama yöntemlerini unutun. isBulsun.com yapay zeka ile size en uygun iş fırsatlarını bulur, başvurularınızı yapar, CV'nizi optimize eder ve kariyerinizi bir üst seviyeye taşır.
        </p>
      </section>

      <section className="hiw__steps">
        <div className="hiw__steps-container">

          <div className="hiw__step">
            <div className="hiw__step-number">01</div>
            <div className="hiw__step-icon">
              <Search size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Akıllı Eşleştirme</h2>
              <p className="hiw__step-desc">
                CV'nizi yükleyin ve ne tür bir iş aradığınızı doğal dille anlatın. Yapay zekamız CV'nizdeki deneyimlerinizi, yeteneklerinizi ve beklentilerinizi analiz ederek size en uygun ilanları bulur.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Sadece size uygun ilanları görürsünüz, binlerce alakasız ilan arasında kaybolmazsınız</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>Yapay zeka tecrübenize, lokasyonunuza ve beklentilerinize göre eşleştirme yapar</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">02</div>
            <div className="hiw__step-icon hiw__step-icon--green">
              <Send size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Otomatik Başvuru</h2>
              <p className="hiw__step-desc">
                Yapay zekanın bulduğu ilanlara ister kendiniz başvurun, isterseniz yapay zekaya bırakın. Yapay zeka sizin adınıza uygun ilanlara otomatik başvuru yapar, siz sadece gelen teklifleri değerlendirin.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>Uygun ilanlar bulunduğunda otomatik başvuru yapılır, zaman kaybetmezsiniz</span>
                </div>
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>Başvurularınızın kontrolü her zaman sizdedir, dilediğiniz zaman manuel geçiş yapabilirsiniz</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">03</div>
            <div className="hiw__step-icon hiw__step-icon--purple">
              <FileCheck size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">İlana Özel CV Optimizasyonu</h2>
              <p className="hiw__step-desc">
                Yapay zeka her ilan için CV'nizi özel olarak optimize eder. İlanın gerektirdiği anahtar kelimeleri, yetkinlikleri ve formatı analiz ederek CV'nizi o ilana en uygun hale getirir. ATS sistemlerinden geçme oranınız katlanarak artar.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Her başvuru için ilanın gereksinimlerine göre özelleştirilmiş CV hazırlanır</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>ATS uyumlu format ve anahtar kelime optimizasyonu ile öne çıkarsınız</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">04</div>
            <div className="hiw__step-icon hiw__step-icon--orange">
              <TrendingUp size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Seviye Ölçümü ve Kariyer Gelişimi</h2>
              <p className="hiw__step-desc">
                Yapay zeka yetkinliklerinizi analiz ederek sektördeki seviyenizi ölçer. Gelişim alanlarınızı belirler, sektör trendlerini takip eder ve kariyerinize yön vermeniz için kişiselleştirilmiş öneriler sunar.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>Sektörünüzdeki seviyenizi ve piyasa değerinizi öğrenin</span>
                </div>
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>Hangi yetkinlikleri geliştirmeniz gerektiğini ve hangi sertifikaların değer katacağını bilin</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">05</div>
            <div className="hiw__step-icon hiw__step-icon--blue">
              <Globe size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Yurtdışı Remote İlanlar</h2>
              <p className="hiw__step-desc">
                Yurtdışındaki remote iş ilanları da listelenir ve eşleştirme sürecine dahil edilir. Yabancı dil bilmenize gerek yok — HireThink Yapay Zeka Teknolojileri'nin geliştirdiği toplantı çevirici ile dil engeli tamamen ortadan kalkar.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Languages size={16} />
                  <span>Karşı tarafın konuşması anlık olarak Türkçe yazıya çevrilir</span>
                </div>
                <div className="hiw__highlight">
                  <Mic size={16} />
                  <span>Siz Türkçe konuşursunuz, yapay zeka konuşmanızı karşı tarafa İngilizce olarak sesli iletir</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__divider">
            <div className="hiw__divider-line"></div>
            <div className="hiw__divider-badge">
              <Plane size={16} />
              <span>ABD &amp; Kanada Fırsatları</span>
            </div>
            <div className="hiw__divider-line"></div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">06</div>
            <div className="hiw__step-icon hiw__step-icon--red">
              <Globe size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">ABD ve Kanada'da İş Fırsatları</h2>
              <p className="hiw__step-desc">
                Amerika ve Kanada'da mavi yaka işçilere büyük talep var. Tır şoförü, aşçı, garson, oto tamircisi, tesisatçı, boyacı, elektrikçi, kaynakçı gibi mesleklerde binlerce açık pozisyon sizi bekliyor. isBulsun yapay zekası bu ilanları sizin için tarar ve eşleştirir.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>ABD ve Kanada'daki mavi yaka iş ilanları sürekli güncellenir</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>Mesleğinize ve deneyiminize göre en uygun pozisyonlar otomatik eşleştirilir</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">07</div>
            <div className="hiw__step-icon hiw__step-icon--orange">
              <Clock size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Geçici Çalışma Vizesi ile Çalışın</h2>
              <p className="hiw__step-desc">
                ABD'de H-2B vizesi, Kanada'da Temporary Foreign Worker Program (TFWP) gibi geçici çalışma programları sayesinde belirli süreli olarak yasal çalışma hakkı elde edebilirsiniz. İşveren sponsorluğunda vize başvurunuz yapılır, konaklama ve ulaşım genellikle işveren tarafından karşılanır.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>Yasal çalışma izni ile güvence altında çalışırsınız</span>
                </div>
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>Vize masrafları ve uçak bileti çoğunlukla işveren tarafından karşılanır</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">08</div>
            <div className="hiw__step-icon hiw__step-icon--green">
              <Home size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Kalıcı Oturum ile Yerleşim İmkanı</h2>
              <p className="hiw__step-desc">
                Kanada'nın Express Entry ve Provincial Nominee programları, ABD'nin EB-3 vizesi gibi yollarla kalıcı oturum izni alabilirsiniz. Mavi yaka mesleklerde deneyimli çalışanlar bu programlarda öncelikli değerlendirilir. Ailenizi de yanınıza alarak yeni bir hayata başlayabilirsiniz.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Kanada'da 2 yıl çalışma deneyimi ile kalıcı oturum başvurusu yapabilirsiniz</span>
                </div>
                <div className="hiw__highlight">
                  <Users size={16} />
                  <span>Eşiniz ve çocuklarınız için de oturum izni alınabilir</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">09</div>
            <div className="hiw__step-icon hiw__step-icon--purple">
              <Wrench size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">En Çok Aranan Mavi Yaka Meslekler</h2>
              <p className="hiw__step-desc">
                Kuzey Amerika'da ciddi işgücü açığı olan meslekler: Uzun yol tır şoförü (CDL), aşçı ve şef, garson, oto tamircisi ve oto boyacı, sıhhi tesisatçı, boya badana ustası, elektrikçi, kaynakçı, inşaat işçisi, çiftlik işçisi, et kesim uzmanı ve paketleme operatörü. Bu mesleklerde tecrübeli adaylar yüksek maaşla işe başlayabilir.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <TrendingUp size={16} />
                  <span>ABD'de tır şoförü yıllık ortalama 55.000–80.000 USD, Kanada'da 50.000–70.000 CAD kazanır</span>
                </div>
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>Aşçı, tesisatçı, elektrikçi gibi mesleklerde kronik işgücü açığı devam ediyor</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">10</div>
            <div className="hiw__step-icon hiw__step-icon--blue">
              <ClipboardCheck size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Vize ve Başvuru Süreç Desteği</h2>
              <p className="hiw__step-desc">
                Yapay zeka vize başvurunuz için gerekli belgeleri listeler, başvuru formlarını doldurmanıza yardımcı olur ve süreç boyunca sizi bilgilendirir. İşveren ile iletişim, LMIA (Kanada) veya Labor Certification (ABD) süreçleri hakkında adım adım rehberlik sağlanır.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>Gerekli belgeler ve başvuru adımları yapay zeka tarafından otomatik hazırlanır</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>Başvuru durumunuz anlık takip edilir, eksik belge olursa hemen bilgilendirilirsiniz</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">11</div>
            <div className="hiw__step-icon hiw__step-icon--red">
              <GraduationCap size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Eğitim, Sertifika ve Oturum Desteği</h2>
              <p className="hiw__step-desc">
                ABD ve Kanada'da birçok mavi yaka meslek için yerel sertifika ve lisans gereklidir. CDL (ticari ehliyet), Red Seal (Kanada zanaat sertifikası), OSHA güvenlik eğitimi, Food Handler sertifikası gibi belgeleri almanız için size rehberlik ediyoruz. Bu sertifikalar hem çalışma izni başvurunuzu güçlendirir hem de kalıcı oturum puanınızı artırır.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Award size={16} />
                  <span>Mesleğinize uygun sertifika programları belirlenir: CDL, Red Seal, OSHA, WHMIS, Food Handler vb.</span>
                </div>
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Sertifikalar kalıcı oturum başvurusunda ek puan kazandırır ve iş tekliflerinizi artırır</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="hiw__cta">
        <div className="hiw__cta-badge">
          <Sparkles size={14} />
          <span>Tamamen Ücretsiz</span>
        </div>
        <h2 className="hiw__cta-title">Hemen Başlayın</h2>
        <p className="hiw__cta-desc">
          Yapay zekanın gücüyle kariyerinizi bir üst seviyeye taşıyın. Hemen iş aramaya başlayın veya üye olarak tüm özelliklerin kilidini açın.
        </p>
        <div className="hiw__cta-actions">
          {user ? (
            <button className="hiw__cta-btn hiw__cta-btn--primary" onClick={() => navigate('/panel/kariyerim')}>
              <ArrowRight size={16} />
              Kariyerime Git
            </button>
          ) : (
            <>
              <button className="hiw__cta-btn hiw__cta-btn--primary" onClick={() => navigate('/')}>
                <Search size={16} />
                İş Aramaya Başla
              </button>
              <button className="hiw__cta-btn hiw__cta-btn--secondary" onClick={() => setIsLoginOpen(true)}>
                Üye Ol
                <ArrowRight size={16} />
              </button>
            </>
          )}
        </div>
        <div className="hiw__cta-features">
          <span>Akıllı Eşleştirme</span>
          <span className="hiw__cta-dot"></span>
          <span>Otomatik Başvuru</span>
          <span className="hiw__cta-dot"></span>
          <span>CV Optimizasyonu</span>
          <span className="hiw__cta-dot"></span>
          <span>Yurtdışı Remote</span>
          <span className="hiw__cta-dot"></span>
          <span>ABD &amp; Kanada İş İmkanı</span>
        </div>
      </section>

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
    </div>
  );
};

export default HowItWorksPage;
